/**
 * React Query hook for the cross-provider admin subscriptions console (#1701).
 *
 * Backed by `GET /v1/admin/subscriptions`, which reads `subscriptionMirrors`
 * — stripe and supapass today, apple/google to follow on the same endpoint.
 */

import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { authenticatedApi } from '../lib/authenticated-api';
import type {
  AdminSubscriptionsPage,
  SubscriptionQueryParams,
} from '../lib/types/subscriptionMirror';

/** Everything the console may filter on. The cursor is the hook's own business. */
export type ListParams = Omit<SubscriptionQueryParams, 'startAfter'>;

/**
 * `scope` keeps two surfaces that can build an IDENTICAL params object out of
 * each other's cache. TanStack hashes query keys structurally with sorted
 * object keys, so the console's `{limit: 100, uid}` and the user card's
 * `{uid, limit: 100}` are the same key. Sharing it would hand the card's
 * deliberately-single-page query the console's whole accumulated
 * `InfiniteData` — and after `staleTime` merely opening the card would refetch
 * every page the console had loaded, in sequence, against the per-admin rate
 * limit. That is exactly the pathology `refetchOnWindowFocus: false` below
 * exists to avoid.
 */
export const subscriptionQueryKeys = {
  all: ['admin', 'subscriptions'] as const,
  list: (params: ListParams, scope: string = 'console') =>
    [...subscriptionQueryKeys.all, 'list', scope, params] as const,
};

/**
 * Paginated listing. The cursor chain is opaque: `nextCursor` is produced by
 * the API from the last row of each page and fed straight back as
 * `startAfter`.
 *
 * ## Two properties of this listing that are not obvious
 *
 * **Paging can omit rows, and cannot duplicate them.** The collection is
 * ordered by `updatedAt DESC` and the cursor is a value cursor over it. Because
 * `updatedAt` only ever moves forward, a row rewritten by a webhook while an
 * admin is paging jumps ABOVE the cursor: if it sat on a page not yet reached,
 * it is skipped for the rest of that pass. Already-seen rows cannot come back
 * round, so duplicates are impossible. The effect is self-correcting — a fresh
 * first page shows the row at the top — which is why the page offers an
 * explicit refresh and stamps what it is showing "as of".
 *
 * **`refetchOnWindowFocus` is disabled here, against the app default.** The
 * default is `import.meta.env.PROD` (see `lib/query-client.ts`), and for an
 * infinite query it refetches EVERY loaded page in sequence. On an admin who
 * alt-tabs back after `staleTime`, that is one request per loaded page against
 * the per-admin rate limit (200 a minute in production when this was written;
 * 6000 since #2465), and the list reorders under them mid-read.
 */
export function useAdminSubscriptionsList(
  params: ListParams,
  options?: { enabled?: boolean; scope?: string }
) {
  return useInfiniteQuery<
    AdminSubscriptionsPage,
    Error,
    InfiniteData<AdminSubscriptionsPage>,
    ReturnType<typeof subscriptionQueryKeys.list>,
    string | undefined
  >({
    queryKey: subscriptionQueryKeys.list(params, options?.scope),
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const response = await authenticatedApi.listSubscriptions({
        ...params,
        startAfter: pageParam,
      });
      // `request()` throws on any non-2xx, so `success` is always true here —
      // only the null case needs narrowing (`data` is `T | null` for 204s).
      if (!response.data) {
        throw new Error(response.error || 'Failed to load subscriptions');
      }
      return response.data;
    },
    // `nextCursor` is present only when the API has more to give, so returning
    // it verbatim is also the "stop paging" signal.
    getNextPageParam: lastPage =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    refetchOnWindowFocus: false,
    // The console always wants this on; the user card (#1705) must NOT fire it
    // with an absent uid. `listSubscriptions` drops a falsy `uid` from the
    // query string rather than sending an empty one, so an unguarded call
    // would return the first page of the WHOLE collection and render another
    // hundred users' subscriptions on one user's card. This flag is the guard.
    enabled: options?.enabled ?? true,
  });
}
